import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { ClassicEditor } from 'ckeditor5';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Blog, getEmptyBlog } from '../../interfaces/Blog/Blog';
import {
  createBlog,
  deleteBlog,
  getBlog,
  updateBlog,
  updateBlogMedia,
} from '../../api/Blog';
import { BlogMediaOptions } from '../../interfaces/Blog/BlogMediaOptions';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import { ImageInput } from '../../components/ImageInput/ImageInput';
import { RichTextEditor } from '../../components/RichTextEditor/RichTextEditor';
import { MetaCard } from '../../components/Meta/MetaCard';
import { MetaTable } from '../../interfaces/Meta/MetaTable';
import { OpenInFrontend } from '../../components/OpenInFrontend/OpenInFrontend';
import { GenerateBlogCoverImageDialog } from './GenerateBlogCoverImageDialog';
import { TranslationAdornment } from '../../components/Translation/TranslationAdornment';
import { LanguageDropdown } from '../../components/Dropdowns/LanguageDropdown';

const width = 900;

export const ModifyBlogPage = () => {
  const navigate = useNavigate();

  const editorRef = useRef<CKEditor<ClassicEditor>>(null);

  const { slug } = useParams<string>();
  const isEditing = !!slug;

  const [blog, setBlog] = useState(getEmptyBlog());
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);
  const [showCoverImageDialog, setShowCoverImageDialog] = useState(false);

  const refreshBlog = () => {
    setLoading(true);
    getBlog(slug!)
      .then((b) => setBlog(b))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshBlog();
    }
  }, [slug]);

  const handleSave = () => {
    const description = editorRef.current?.editor?.getData() ?? '';
    const resultingBlog: Blog = { ...blog, description };

    if (isEditing) {
      setLoading(true);
      updateBlog(resultingBlog)
        .then((updatedBlog) => {
          toast.success('Blog başarıyla güncellendi');
          if (blog.slug === updatedBlog.slug) {
            setBlog(updatedBlog);
          } else {
            navigate(`/blogs/blogs/${updatedBlog.slug}`);
          }
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createBlog(resultingBlog)
        .then((createdBlog) => {
          toast.success('Blog başarıyla oluşturuldu');
          navigate(`/blogs/blogs/${createdBlog.slug}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleUpdateMedia = (options: BlogMediaOptions) => {
    setLoading(true);
    updateBlogMedia(blog.id, options)
      .then((updatedBlog) => {
        toast.success('Medya başarıyla güncellendi');
        setBlog(updatedBlog);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteBlog(blog.id)
      .then(() => {
        toast.success('Blog başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/blogs/blogs');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili blogu silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}

      <GenerateBlogCoverImageDialog
        blog={blog}
        open={showCoverImageDialog}
        onClose={() => setShowCoverImageDialog(false)}
        onGenerate={(file) => handleUpdateMedia({ coverImage: file })}
      />

      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>

        <Typography variant='h5'>
          {isEditing ? 'Blogu Düzenle' : 'Yeni Blog'}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {!!slug && <OpenInFrontend path='blog' slug={slug} isActive />}

        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          height: 'fit-content',
          width: 'fit-content',
          p: 3,
          gap: 1.5,
          boxSizing: 'border-box',
        }}
      >
        <TextField
          label='Başlık'
          variant='outlined'
          size='small'
          multiline
          value={blog.title}
          onChange={(e) => setBlog({ ...blog, title: e.target.value })}
          sx={{ width }}
          InputProps={{
            endAdornment: (
              <TranslationAdornment
                multiline
                defaultValue={blog.title}
                value={blog.titleTranslation}
                onChange={(x) => setBlog({ ...blog, titleTranslation: x })}
              />
            ),
          }}
        />
        <TextField
          label='Kısa Açıklama'
          variant='outlined'
          size='small'
          multiline
          value={blog.shortDescription}
          onChange={(e) =>
            setBlog({ ...blog, shortDescription: e.target.value })
          }
          sx={{ width }}
          InputProps={{
            endAdornment: (
              <TranslationAdornment
                multiline
                defaultValue={blog.shortDescription}
                value={blog.shortDescriptionTranslation}
                onChange={(x) =>
                  setBlog({ ...blog, shortDescriptionTranslation: x })
                }
              />
            ),
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant='h6'>İçerik</Typography>

          <TranslationAdornment
            richText
            defaultValue={blog.description}
            value={blog.descriptionTranslation}
            onChange={(x) => setBlog({ ...blog, descriptionTranslation: x })}
          />
        </Box>

        {!loading && (
          <RichTextEditor
            editorRef={editorRef}
            placeholder='Blog içeriği'
            initialData={blog.description}
            width={width}
          />
        )}

        <LanguageDropdown
          label='Hedef Dil'
          allowDefault
          defaultOptionLabel='Tüm Diller'
          value={blog.locale ?? null}
          onChange={(x) => setBlog({ ...blog, locale: x ?? undefined })}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
          }}
        >
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ ...sharedStyles.buttonText }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>
      {isEditing && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            mt: 1.5,
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          {blog.id && <MetaCard table={MetaTable.BLOG} id={blog.id} />}

          <ImageInput
            title='Kapak Resmi'
            existingImage={blog.coverImage}
            onClickGenerate={() => setShowCoverImageDialog(true)}
            onUpload={(file) => handleUpdateMedia({ coverImage: file })}
            onDelete={() => handleUpdateMedia({ eraseCoverImage: true })}
          />
        </Box>
      )}
    </Box>
  );
};
